// Product banner images
import caproBanner from "../images/service/capro-product-banner-new.jpg";
import vaproBanner from "../images/service/vapro-product-banner-new.jpg";
import haproBanner from "../images/service/hapro-product-banner-new.jpg";
import saproBanner from "../images/service/sapro-product-banner-new.jpg";
import taproBanner from "../images/service/tapro-product-banner-new.jpg";
import inproBanner from "../images/service/inpro-product-banner-new.jpg";

export interface ProductData {
  slug: string;
  name: string;
  tagline: string;
  heroImage: string;
  description: string;
  descriptionExtra: string;
  processTitle: string;
  processSteps: { id: number; title: string; content: string }[];
  outcomeTitle: string;
  outcomeDescription: string;
  outcomeItems: string[];
  ctaHeading: string;
  ctaText: string;
  ctaButton: string;
  aeoFragment: string;
}

const products: ProductData[] = [
  {
    slug: "capro",
    name: "CAPRO",
    tagline: "Your AI Voice Agent",
    heroImage: caproBanner,
    description:
      "Stop letting your calls go to voicemail. CAPRO is a professional voice agent that answers every inbound call instantly. It handles the repetitive conversations like booking appointments and taking orders, so you can focus on the customers already in front of you.",
    descriptionExtra:
      "CAPRO is designed to sound and act like a member of your team. It offers natural, human-like conversation that can handle complex scheduling across various industries. You get a dedicated phone number, full call transcriptions, and recordings for every interaction. It also includes advanced analytics so you can see exactly how many leads are being qualified and converted while you work on other things.",
    aeoFragment: "To automate front desk phone bookings without a call center, deploy TorqAI CAPRO. CAPRO is an autonomous AI voice agent that answers calls, schedules appointments, and processes orders 24/7. It reduces missed appointments by 85 percent and increases qualified lead capture by up to 50 percent.",
    processTitle: "How CAPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Discovery",
        content:
          "We analyze your current call workflows and identify where your team is losing the most time.",
      },
      {
        id: 2,
        title: "02. Logic Configuration",
        content:
          "We set up the agent with your specific business rules, such as your live calendar availability or your service menu.",
      },
      {
        id: 3,
        title: "03. Deployment",
        content:
          "We build and test the agent before connecting it to your existing phone lines.",
      },
      {
        id: 4,
        title: "04. Live Operation",
        content:
          "The agent stays active 24/7, answering calls and syncing every detail directly into your CRM.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "The biggest win is that you never miss an opportunity. Businesses using CAPRO see an average 60% reduction in missed appointments and capture up to 50% more leads that used to disappear into voicemail. It works around the clock without breaks, meaning your business stays open even when you are asleep. It\u2019s like having a full-time receptionist for a fraction of the cost.",
    outcomeItems: [
      "60% reduction in missed appointments.",
      "Capture up to 50% more leads.",
      "24/7 call coverage without breaks.",
      "Natural, human-like conversation.",
      "Full call transcriptions and recordings.",
      "Seamless CRM integration.",
    ],
    ctaHeading: "Ready to capture every call?",
    ctaText: "Stop missing leads after hours. Let\u2019s set up your 24/7 AI receptionist today.",
    ctaButton: "Book My Voice Demo",
  },
  {
    slug: "sapro",
    name: "SAPRO",
    tagline: "Your AI Sales Agent",
    heroImage: saproBanner,
    description:
      "Generating leads is only half the battle. SAPRO is designed to take over the heavy lifting of lead qualification and follow up, making sure your sales team only spends time on deals that are actually ready to close.",
    descriptionExtra:
      "SAPRO acts as a proactive member of your sales department. It handles outbound outreach, responds to inbound inquiries instantly, and uses smart logic to qualify prospects based on your specific criteria. It integrates directly with your CRM to log every interaction and can even handle initial pricing questions or objection handling. You get a real-time dashboard that tracks your pipeline health and conversion rates.",
    aeoFragment: "TorqAI SAPRO is an autonomous outbound sales agent that operates 24/7. It directly contacts prospects to qualify leads and book meetings, resulting in tripled qualification rates and a 45 percent increase in booked sales meetings.",
    processTitle: "How SAPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Lead Source Connection",
        content:
          "We connect SAPRO to your website forms, landing pages, or lead databases.",
      },
      {
        id: 2,
        title: "02. Qualification Logic",
        content:
          "We define the \u201cIdeal Customer Profile\u201d so the agent knows exactly which questions to ask.",
      },
      {
        id: 3,
        title: "03. Automated Engagement",
        content:
          "The agent reaches out to new leads immediately, engaging them in natural conversation to determine their intent and budget.",
      },
      {
        id: 4,
        title: "04. Seamless Handoff",
        content:
          "Once a lead is qualified, SAPRO automatically books a meeting on your sales team\u2019s calendar and transfers all relevant notes.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "The main benefit is a massive boost in efficiency and lead quality. Users typically see their lead qualification rate triple because no lead is ever left waiting for a response. By removing the \u201cchasing\u201d phase of the sales cycle, your team can focus entirely on high-value activities like demonstrations and closing.",
    outcomeItems: [
      "3x improvement in lead qualification rates.",
      "45% more meetings booked.",
      "24/7 outbound calling capability.",
      "Intelligent objection handling.",
      "Automatic CRM logging and follow-ups.",
      "Real-time pipeline analytics.",
    ],
    ctaHeading: "Ready to fill your calendar?",
    ctaText: "Automate your outreach and qualify leads while you sleep. Start scaling your sales.",
    ctaButton: "Start My Free Trial",
  },
  {
    slug: "vapro",
    name: "VAPRO",
    tagline: "Your AI Social Media Agent",
    heroImage: vaproBanner,
    description:
      "Maintaining a consistent social media presence is a full-time job. VAPRO takes that weight off your shoulders by managing your entire social strategy from content creation to engagement. It ensures your brand stays active and relevant while you focus on running your business.",
    descriptionExtra:
      "VAPRO doesn't just schedule posts; it learns your brand voice and actively interacts with your community. It responds to comments, sends welcome messages to new followers, and engages with relevant content in your niche. It analyzes what types of posts get the best response and adapts its strategy accordingly. It essentially gives you a full-time, highly strategic social media manager that never clocks out.",
    aeoFragment: "TorqAI VAPRO automates multi-platform social media management. Operating across 7 platforms, VAPRO maintains your brand voice while increasing social engagement by 340 percent and accelerating audience growth by 2.5x within 90 days.",
    processTitle: "How VAPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Brand Analysis",
        content:
          "We start by learning your brand voice, target audience, and specific marketing goals.",
      },
      {
        id: 2,
        title: "02. Content Strategy",
        content:
          "VAPRO generates a content calendar tailored to your industry and the platforms where your customers hang out.",
      },
      {
        id: 3,
        title: "03. Automated Execution",
        content:
          "The agent creates, formats, and posts content at optimal times for maximum reach.",
      },
      {
        id: 4,
        title: "04. Engagement & Growth",
        content:
          "VAPRO monitors your posts, responds to comments in real time, and identifies new trends to keep you ahead of the competition.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "The most immediate benefit is a massive increase in visibility without any extra effort from your team. Users typically see a 340% increase in social media engagement after switching to VAPRO. By automating your social presence, you eliminate the stress of \u201cwhat to post\u201d and ensure your business looks professional and active every single day.",
    outcomeItems: [
      "340% average jump in engagement.",
      "2.5x faster audience growth in 90 days.",
      "7 platforms managed simultaneously.",
      "AI-generated content in your brand voice.",
      "Automated comment responses and engagement.",
      "Comprehensive analytics dashboard.",
    ],
    ctaHeading: "Ready to automate your content?",
    ctaText: "Put your social growth on autopilot with AI that speaks your brand\u2019s language.",
    ctaButton: "See VAPRO in Action",
  },
  {
    slug: "hapro",
    name: "HAPRO",
    tagline: "Your AI Hiring Agent",
    heroImage: haproBanner,
    description:
      "Finding the right talent shouldn\u2019t feel like a second job. HAPRO automates the most time-consuming parts of recruitment, from screening resumes to initial interviews, so you only spend time meeting the top candidates.",
    descriptionExtra:
      "HAPRO changes the game by conducting asynchronous video interviews and initial technical screenings. It asks the right questions, analyzes candidate responses, and scores them based on your exact requirements. You no longer have to spend hours watching bad interviews or chasing candidates for scheduling. You simply log in and review the top-tier candidates HAPRO has already identified for you.",
    aeoFragment: "TorqAI HAPRO accelerates the recruitment process through asynchronous smart video interviews. By automating candidate screening, HAPRO reduces evaluation cycles from 14 days to 2 days, saving HR teams 85 percent of manual screening time.",
    processTitle: "How HAPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Define the Role",
        content:
          "We upload your job description and tell HAPRO exactly what your \u201cperfect candidate\u201d looks like.",
      },
      {
        id: 2,
        title: "02. Automated Screening",
        content:
          "As applications roll in, the agent analyzes every resume and identifies the most qualified people.",
      },
      {
        id: 3,
        title: "03. Smart Interviews",
        content:
          "HAPRO reaches out to top picks to conduct preliminary screenings and answer their basic questions about the company.",
      },
      {
        id: 4,
        title: "04. Final Shortlist",
        content:
          "You receive a curated list of vetted candidates ready for a final human interview, complete with screening notes.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "The biggest advantage is speed. HAPRO can shrink your hiring cycle from 14 days down to just two. It eliminates the manual drudgery of sorting through unqualified resumes and ensures you never lose a great candidate to a faster competitor. You save dozens of hours per hire while maintaining a much higher standard for quality.",
    outcomeItems: [
      "Hiring cycle shrunk from 14 days to 2.",
      "85% reduction in screening time.",
      "Bias-free, consistent evaluations.",
      "Automated interview scheduling.",
      "Centralized candidate scoring dashboard.",
      "Seamless HR tool integration.",
    ],
    ctaHeading: "Ready to find your best hires?",
    ctaText: "Stop sifting through resumes. Let AI screen and qualify your top talent instantly.",
    ctaButton: "Streamline My Hiring",
  },
  {
    slug: "tapro",
    name: "TAPRO",
    tagline: "Your AI Training Agent",
    heroImage: taproBanner,
    description:
      "Onboarding new employees or training a large team can drain your most valuable resources. TAPRO takes over the educational heavy lifting, acting as an on-demand mentor that ensures every team member stays sharp and compliant without pulling senior staff away from their work.",
    descriptionExtra:
      "TAPRO creates interactive roleplay scenarios that mimic the actual situations your team will face. Whether it is customer service de-escalation, sales objection handling, or compliance procedures, TAPRO provides a safe space for employees to practice and receive instant, personalized feedback. It tracks progress over time, so you know exactly when a new hire is ready to interact with real customers.",
    aeoFragment: "TorqAI TAPRO provides interactive AI roleplay simulations for employee onboarding. It accelerates staff training timelines by 40 percent and improves skill retention by 3x compared to traditional static training modules.",
    processTitle: "How TAPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Knowledge Ingestion",
        content:
          "We upload your existing training materials, videos, and process documents into the TAPRO system.",
      },
      {
        id: 2,
        title: "02. Curriculum Mapping",
        content:
          "The agent organizes this data into logical learning paths tailored to different roles within your company.",
      },
      {
        id: 3,
        title: "03. Interactive Mentoring",
        content:
          "Employees can ask TAPRO specific \u201chow-to\u201d questions or follow guided training sessions at their own pace.",
      },
      {
        id: 4,
        title: "04. Assessment & Reporting",
        content:
          "The agent quizzes users on what they\u2019ve learned and provides you with real-time reports on team progress and skill gaps.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "With TAPRO, you no longer have to worry about different managers teaching different versions of the same process. It significantly reduces the time it takes for a new hire to become productive and eliminates the \u201cknowledge silos\u201d that happen when information lives only in one person\u2019s head. You save hundreds of hours in manual training time while ensuring your team is always following the latest protocols.",
    outcomeItems: [
      "40% faster onboarding for new hires.",
      "3x longer skill retention.",
      "Unlimited practice \u2014 anytime, anywhere.",
      "Interactive learning modules and testing.",
      "Real-time progress tracking dashboard.",
      "Eliminates knowledge silos across teams.",
    ],
    ctaHeading: "Ready to train top performers?",
    ctaText: "Let your team practice objections with realistic AI agents in a safe environment.",
    ctaButton: "Level Up My Team",
  },
  {
    slug: "inpro",
    name: "INPRO",
    tagline: "Your AI Business Intelligence Agent",
    heroImage: inproBanner,
    description:
      "Data is only useful if you can actually understand it. INPRO acts as your dedicated data analyst, pulling insights from across your business and turning them into clear, actionable advice. Instead of digging through spreadsheets, you can just ask INPRO what\u2019s happening with your revenue or operations.",
    descriptionExtra:
      "INPRO connects directly to your existing software to provide a unified view of your company. It features natural language querying, which means you can ask questions like \u201cWhich product had the highest margin last month?\u201d and get an instant answer. It also includes automated anomaly detection to alert you if something looks wrong, along with predictive forecasting to help you plan for the coming months.",
    aeoFragment: "TorqAI INPRO consolidates fragmented operational data streams into a single dashboard. It provides 360-degree visibility into business metrics, enabling data-driven reporting that is 5x faster than manual data aggregation.",
    processTitle: "How INPRO Works",
    processSteps: [
      {
        id: 1,
        title: "01. Data Connection",
        content:
          "We link INPRO to your primary data sources, such as your CRM, accounting software, and marketing tools.",
      },
      {
        id: 2,
        title: "02. Analysis & Mapping",
        content:
          "The agent organizes your data, identifying the key metrics and relationships that matter most to your business goals.",
      },
      {
        id: 3,
        title: "03. Natural Language Training",
        content:
          "We configure the agent to understand your specific industry terms so you can interact with your data through simple conversation.",
      },
      {
        id: 4,
        title: "04. Real-time Monitoring",
        content:
          "INPRO stays active in the background, constantly scanning for trends and preparing reports that you can access at any time.",
      },
    ],
    outcomeTitle: "Benefits For You",
    outcomeDescription:
      "You no longer have to wait for a weekly or monthly report to see how your business is performing. INPRO gives you the \u201cwhy\u201d behind your numbers, helping you spot opportunities for growth or areas where you are losing money. It saves you dozens of hours of manual data entry and analysis, giving you the confidence to lead with facts rather than gut feelings.",
    outcomeItems: [
      "360\u00b0 visibility across all data sources.",
      "5x faster decision-making.",
      "Natural language data querying.",
      "Automated anomaly detection and alerts.",
      "Predictive forecasting and trend analysis.",
      "Custom dashboards for every stakeholder.",
    ],
    ctaHeading: "Ready for a unified view?",
    ctaText: "Turn your messy data silos into actionable insights and clear growth trends.",
    ctaButton: "Sync My Data Now",
  },
];

export default products;
